/* eslint-disable no-inline-styles/no-inline-styles */
import React, { Fragment } from "react";
import { Toggle } from "@fluentui/react";
import styled from "styled-components";
import { IDetailsList } from "@cayo/shared/api/types/schema";
import { isString } from "@cayo/shared/types/typeGuards";
import { nameofFactory, objectUtils } from "@cayo/shared/utils/object";
import { formatValue, lookupIcon, propertyIcon } from "./common";
import { getFormatterFromRenderOptions } from "../../../utils/formatter-utils";
import ErrorTooltip from "@cayo/shared/components/common/ErrorTooltip";
import Icon from "../../Graphics/Icon";
import MaskedIcon from "../../Graphics/MaskedIcon";
import { IconEx } from "../../LegacyDetailsList/cell-renderers";
import RenderOptionsFormatter from "../utils/field-formatter";
import { objectTypeToTitle } from "../utils/object-type-to-title";
import CloudIcon from "./../../../images/cloud.svg";
import GridCell from "./GridCell";
import { RenderCellFn } from "./types";

//const log = logger.getLogger("renderIconAndTextCell");

const showWhenNone = ["lastVerificationStatus", "lastRollbackResult"];

export const renderIconAndTextCell: RenderCellFn = ({
  item,
  column,
  auxProps,
  readOnly,
  onItemClicked,
}) => {
  const renderOptions = column.renderOptions || {};
  const fieldFormatter = new RenderOptionsFormatter(renderOptions, getFormatterFromRenderOptions);
  const invokeItemByOneClick =
    auxProps?.additionalProps &&
    auxProps?.additionalProps[detailsListNames("invokeItemByOneClick")];

  const isNone =
    item[column.fieldName!] === "none" && !showWhenNone.includes(column.fieldName || "");
  if (isNone) {
    return <Fragment />;
  }

  let isColumnWithImage =
    !(column as any).noIcon &&
    ((column.fieldName === "name" && item.objectType) ||
      (column.fieldName === "objectName" && (item.objectType || item.nestedType)) ||
      (column.fieldName === "displayName" && item.objectType && !item.name) ||
      fieldFormatter.isIconWithText());

  if (column.fieldName === "displayName" && !!item.objectName && !!item.objectType) {
    isColumnWithImage = false;
  }

  if (!isColumnWithImage) {
    return null;
  }

  const objType = item.targetObjectType || item.nestedType || item.objectType || "";
  const addSwitch = !!column.renderOptions?.switchProperty;

  const isCloud =
    objType.toLowerCase().indexOf("microsoft.graph") !== -1 ||
    objType.toLowerCase().indexOf("cayo.azurerm") !== -1 ||
    (objType === "cayo.graph.authorityPrincipal" && item.source === "azureAD") ||
    objType.toLowerCase().indexOf("microsoft.exo") !== -1;

  const typeAnnotation = auxProps?.getTypeAnnotation!(objType);

  const iconSource = fieldFormatter.iconSource;

  let iconAnnotation: IconEx | undefined = lookupIcon(
    item,
    auxProps!,
    iconSource ?? column.fieldName
  );

  let addCloudIcon = !iconSource && isCloud && !iconAnnotation?.isIconOnly;
  //HARDCODE: BUG 11193 : show cloud icon for threat alert
  if (item.objectType === "cayo.graph.riskAlert" && column.fieldName === "targetObjectName") {
    addCloudIcon = isCloud;
  }

  // log.debug("iconAnnotation", iconAnnotation);
  let isIconType = false;
  if (!iconAnnotation) {
    iconAnnotation = typeAnnotation?.icon;
    isIconType = true;
  }

  const iconTitle =
    iconAnnotation?.title ||
    (column.fieldName === "targetObjectName" && iconSource !== "targetObjectType" // TODO: remove hardcode
      ? objectTypeToTitle(objType)
      : iconSource && isString(item[iconSource])
        ? item[iconSource]
        : item?.name || undefined);

  let icon =
    (iconAnnotation && propertyIcon(iconAnnotation, iconTitle)) ||
    propertyIcon(
      {
        iconId: objectUtils.isCollection(objType) ? "FabricFolder" : "Document",
      },
      iconTitle
    );

  if (addCloudIcon) {
    icon = (
      <MaskedIcon maskIcon={<img alt={item.name} src={CloudIcon} width={9} height="9" />}>
        {icon}
      </MaskedIcon>
    );
  }

  //error
  if (item?.errors?.length > 0 && !isIconType) {
    icon = (
      <ErrorTooltip id={item.id} errors={item.errors}>
        {icon}
      </ErrorTooltip>
    );
  }
  if (column.fieldName === "name" && item.disabled) {
    icon = (
      <Icon
        iconName={"DRM"}
        style={{
          padding: "0 4px 0 0",
          fontSize: "14px",
          color: auxProps?.theme?.cayoTheme.brandColors.disabled,
        }}
      />
    );
  }

  const rawValue = item[column.fieldName!];
  const formattedValue = formatValue(
    item,
    column.fieldName!,
    { itemFormatValue: item.formatValue, ...renderOptions },
    auxProps
  );

  return (
    <GridCell key={item.id || item.index} renderer="text-with-icon">
      <ToggleWrapper>
        {addSwitch && (
          <Toggle
            onChange={async () => {
              return (
                onItemClicked &&
                (await onItemClicked(
                  undefined,
                  undefined,
                  (() => {
                    item._hasToggle = true;
                    return item;
                  })()
                ))
              );
            }}
            disabled={readOnly || item.mandatory}
            checked={!item.disabled}
            styles={{ root: { marginBottom: 0, marginRight: 8 } }}
          />
        )}
        <IconWrapper>{icon}</IconWrapper>
        <HoveredItem underline={invokeItemByOneClick} data-to-copy>
          {formattedValue || rawValue}
        </HoveredItem>
      </ToggleWrapper>
    </GridCell>
  );
};

const detailsListNames = nameofFactory<IDetailsList>();

const HoveredItem = styled.span<{ underline?: boolean }>`
  max-width: 100%;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 4px;

  &:hover {
    text-decoration: ${(props) => (props.underline ? "underline" : undefined)};
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  margin-right: 4px;
`;
